import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1150, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', whiteSpace: 'nowrap' as const },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, width: '100%' } as React.CSSProperties,
};

const scoreColor = (s: number) => s >= 70 ? '#188038' : s >= 45 ? '#e8710a' : '#d93025';
const DIM_NAMES: Record<string, string> = {
  specificity: t('具体性'), problem_solution: t('问题-方案'), comparison: t('对比要素'),
  faq_coverage: t('FAQ 覆盖'), use_cases: t('使用场景'), trust: t('信任要素'),
};

// ═══ 诊断 ═══
function DiagnoseView() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [rewriting, setRewriting] = useState('');

  const load = () => get('/api/v1/diagnose').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const diagnose = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/diagnose', { limit: 20 }); alert(`诊断完成：${r.data.products_diagnosed} 个商品`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const rewrite = async (pid: string) => {
    setRewriting(pid);
    try { await post(`/api/v1/rewrites/${encodeURIComponent(pid)}`); alert('重写完成，请到"重写审核"页查看'); }
    catch (e: any) { alert(e.message); }
    setRewriting('');
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={diagnose}>{busy ? '诊断中（约 1 分钟）...' : t('🩺 诊断全店 PDP')}</button>
      </div>
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('总分')}</th><th style={S.th}>{t('分项')}</th><th style={S.th}>{t('操作')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => {
              const scores = JSON.parse(r.scores_json || '{}');
              return (
                <tr key={r.product_id}>
                  <td style={{ ...S.td, maxWidth: 240 }}>{r.title}</td>
                  <td style={{ ...S.td, fontSize: 18, fontWeight: 700, color: scoreColor(r.total_score) }}>{Math.round(r.total_score)}</td>
                  <td style={{ ...S.td, fontSize: 11 }}>
                    {Object.entries(scores).map(([k, v]: any) => (
                      <span key={k} style={{ marginRight: 8, color: scoreColor(v) }}>{DIM_NAMES[k] || k}:{Math.round(v)}</span>
                    ))}
                  </td>
                  <td style={S.td}>
                    <button style={S.btnPrimary} disabled={rewriting === r.product_id} onClick={() => rewrite(r.product_id)}>
                      {rewriting === r.product_id ? '重写中...' : t('✍️ 双优重写')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td style={S.td} colSpan={4}>{t('暂无诊断 — 点击"诊断全店 PDP"')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 重写审核 ═══
function RewritesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);

  const load = () => get('/api/v1/rewrites').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (id: string) => {
    try { const r: any = await get(`/api/v1/rewrites/${id}`); setDetail(r.data); } catch (e: any) { alert(e.message); }
  };

  const act = async (id: string, action: 'apply' | 'rollback') => {
    try { await post(`/api/v1/rewrites/${id}/${action}`); alert(action === 'apply' ? '已回写 Shopify' : t('已回滚')); setDetail(null); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      {detail && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong>{detail.title} — diff 预览（声音一致性 {detail.voice_score ?? '-'}/100）</strong>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {detail.status === 'draft' && <button style={S.btnPrimary} onClick={() => act(detail.id, 'apply')}>{t('✓ 回写 Shopify')}</button>}
              {detail.status === 'applied' && <button style={S.btn} onClick={() => act(detail.id, 'rollback')}>{t('↩ 回滚')}</button>}
              <button style={S.btn} onClick={() => setDetail(null)}>{t('关闭')}</button>
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#d93025', fontWeight: 600, marginBottom: 4 }}>{t('BEFORE（原版）')}</div>
              <div style={{ fontSize: 12, background: '#fff', padding: 10, borderRadius: 6, maxHeight: 240, overflow: 'auto' }}
                dangerouslySetInnerHTML={{ __html: detail.original_html || t('(空)') }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#188038', fontWeight: 600, marginBottom: 4 }}>{t('AFTER（人类版）')}</div>
              <div style={{ fontSize: 12, background: '#fff', padding: 10, borderRadius: 6, maxHeight: 240, overflow: 'auto' }}
                dangerouslySetInnerHTML={{ __html: detail.human_version || '' }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('AI 结构化版')}</div>
            <pre style={{ fontSize: 11, background: '#fff', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{detail.ai_version}</pre>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>FAQ（{JSON.parse(detail.faqs_json || '[]').length} 条）</div>
              <div style={{ fontSize: 11, background: '#fff', padding: 10, borderRadius: 6, maxHeight: 160, overflow: 'auto' }}>
                {JSON.parse(detail.faqs_json || '[]').map((f: any, i: number) => (
                  <div key={i} style={{ marginBottom: 6 }}><strong>Q: {f.q}</strong><br />A: {f.a}</div>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('规格表')}</div>
              <table style={{ ...S.table, fontSize: 11, background: '#fff' }}>
                <tbody>
                  {JSON.parse(detail.spec_table_json || '[]').map((s: any, i: number) => (
                    <tr key={i}><td style={S.td}>{s.name}</td><td style={S.td}>{s.value}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('声音分')}</th><th style={S.th}>{t('合规标记')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('时间')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => {
              const flags = JSON.parse(r.compliance_flags_json || '[]');
              return (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
                  <td style={S.td}>{r.title}</td>
                  <td style={S.td}>{r.voice_score ?? '-'}</td>
                  <td style={{ ...S.td, color: flags.length ? '#d93025' : '#188038' }}>{flags.length ? `⚠ ${flags.length}` : '✓'}</td>
                  <td style={S.td}>{r.status}</td>
                  <td style={S.td}>{(r.created_at || '').slice(0, 16)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无重写记录')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 品牌声音 ═══
function VoiceView() {
  const [voice, setVoice] = useState<any>(null);

  useEffect(() => { get('/api/v1/voice').then((r: any) => setVoice(r.data)).catch(() => {}); }, []);
  if (!voice) return <div style={S.card}>{t('加载中...')}</div>;

  const save = async () => {
    try { await put('/api/v1/voice', voice); alert('已保存'); } catch (e: any) { alert(e.message); }
  };

  return (
    <div style={{ ...S.card, maxWidth: 560 }}>
      <h3 style={{ fontSize: 15, marginBottom: 16 }}>{t('品牌声音档案')}</h3>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{t('口吻')}</label>
        <select style={S.input} value={voice.tone} onChange={e => setVoice({ ...voice, tone: e.target.value })}>
          <option value="professional">{t('专业')}</option><option value="formal">{t('正式')}</option>
          <option value="casual">{t('轻松')}</option><option value="playful">{t('年轻活泼')}</option>
        </select>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{t('品牌专词（锁定不改写，逗号分隔）')}</label>
        <input style={S.input} value={voice.locked_words} onChange={e => setVoice({ ...voice, locked_words: e.target.value })} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{t('禁用词黑名单（逗号分隔）')}</label>
        <input style={S.input} value={voice.banned_words} onChange={e => setVoice({ ...voice, banned_words: e.target.value })} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{t('品牌口吻范例文本')}</label>
        <textarea style={{ ...S.input, height: 100 }} value={voice.sample_text} onChange={e => setVoice({ ...voice, sample_text: e.target.value })} />
      </div>
      <button style={S.btnPrimary} onClick={save}>{t('保存档案')}</button>
    </div>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('diagnose');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>PDPAIRewriter</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('产品页 AI 可读性重写 — 6 维诊断 · 双优重写 · 品牌声音保护 · 一键回滚')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'diagnose')} onClick={() => setTab('diagnose')}>{t('就绪度诊断')}</div>
        <div style={S.tab(tab === 'rewrites')} onClick={() => setTab('rewrites')}>{t('重写审核')}</div>
        <div style={S.tab(tab === 'voice')} onClick={() => setTab('voice')}>{t('品牌声音')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'diagnose' && <DiagnoseView />}
      {tab === 'rewrites' && <RewritesView />}
      {tab === 'voice' && <VoiceView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
